// App Improvements: every request on a board by status.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiErrorMessage } from "../../api";
import FeedbackModal from "../../components/feedback/FeedbackModal";
import {
  CATEGORIES, PRIORITIES, STATUSES, listFeedback, lookup, screenshotUrl,
} from "../../components/feedback/feedbackApi";

export function CategoryChip({ value }) {
  const c = lookup(CATEGORIES, value);
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

export function PriorityFlag({ value }) {
  const p = lookup(PRIORITIES, value);
  return <span title={`${p.label} priority`} style={{ color: p.color, fontSize: 12, fontWeight: 600 }}>⚑ {p.label}</span>;
}

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

function Card({ item, me }) {
  const thumb = item.screenshots?.find((s) => s.kind === "ORIGINAL") || item.screenshots?.[0];
  const waitingOnMe = item.status === "READY_FOR_REVIEW" && item.submitted_by === me?.id;
  return (
    <Link to={`/improvements/${item.id}`} data-testid={`feedback-card-${item.id}`}
      style={{
        display: "block", background: "#fff", borderRadius: 10, padding: 12, textDecoration: "none",
        color: "#111827", border: waitingOnMe ? "2px solid #7c3aed" : "1px solid #e5e7eb",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
        <span>#{item.id} · <CategoryChip value={item.category} /></span>
        <span>{fmtDate(item.created_at)}</span>
      </div>
      <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.35, marginBottom: 8 }}>{item.title}</div>
      {thumb && (
        <img src={screenshotUrl(thumb.id)} alt="" loading="lazy"
          style={{ width: "100%", height: 90, objectFit: "cover", objectPosition: "top", borderRadius: 6, border: "1px solid #f3f4f6", marginBottom: 8 }} />
      )}
      {waitingOnMe && (
        <div style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, marginBottom: 6 }}>Waiting for your sign-off</div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280" }}>
        <PriorityFlag value={item.priority} />
        <span>💬 {item.comment_count || 0} · {item.submitted_by_name}</span>
      </div>
    </Link>
  );
}

export default function ImprovementsPage({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [category, setCategory] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await listFeedback());
      setError("");
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load improvement requests."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const reload = () => load();
    window.addEventListener("feedback-changed", reload);
    return () => window.removeEventListener("feedback-changed", reload);
  }, [load]);

  const visible = useMemo(() => items.filter((i) =>
    (!mineOnly || i.submitted_by === user?.id) && (!category || i.category === category)),
  [items, mineOnly, category, user]);

  const columns = STATUSES.filter((s) => showResolved || s.value !== "RESOLVED");
  const resolvedCount = visible.filter((i) => i.status === "RESOLVED").length;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>App Improvements</h1>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setCreating(true)}
          style={{ background: "#4f46e5", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 600, cursor: "pointer" }}>
          + New request
        </button>
      </div>
      <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 16px" }}>
        Spot something on any screen? Press <strong>💬 Feedback</strong> in the bottom-right corner to
        screenshot it, mark it up and say what should change. When it's done you'll be asked to approve it.
      </p>

      <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 16, fontSize: 13, flexWrap: "wrap" }}>
        <label><input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} /> Only mine</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #d1d5db" }} aria-label="Type">
          <option value="">All types</option>
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <label><input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} /> Show resolved ({resolvedCount})</label>
      </div>

      {error && <div role="alert" style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>}
      {loading ? (
        <div style={{ color: "#6b7280" }}>Loading…</div>
      ) : (
        <div style={{
          display: "grid", gap: 12, alignItems: "start",
          gridTemplateColumns: `repeat(${columns.length}, minmax(220px, 1fr))`, overflowX: "auto",
        }}>
          {columns.map((col) => {
            const colItems = visible.filter((i) => i.status === col.value);
            return (
              <section key={col.value} data-testid={`feedback-col-${col.value}`}
                style={{ background: "#f3f4f6", borderRadius: 12, padding: 10, minHeight: 120 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color }} />
                  {col.label}
                  <span style={{ background: "#e5e7eb", borderRadius: 6, padding: "0 6px", fontSize: 11, color: "#4b5563" }}>
                    {colItems.length}
                  </span>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {colItems.map((i) => <Card key={i.id} item={i} me={user} />)}
                  {!colItems.length && <div style={{ color: "#9ca3af", fontSize: 12, padding: 6 }}>Nothing here</div>}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {creating && (
        <FeedbackModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}
